/**
 * Combobox — searchable single-select via cmdk + Popover.
 *
 * Used by Specials team picker (48-team WC roster) — replaces raw <select>.
 *
 * Compose:
 *   <Combobox
 *     value={teamCode}
 *     onValueChange={setTeamCode}
 *     options={[{ value: 'BRA', label: 'Brasil', icon: '🇧🇷' }, ...]}
 *     placeholder="Selecione um time"
 *   />
 */

import { useState } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from './popover';

export interface ComboboxOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  /** Free-text used for search matching in addition to label. */
  keywords?: string[];
}

export interface ComboboxProps {
  value?: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
  triggerClassName?: string;
}

export default function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Selecione…',
  emptyMessage = 'Nenhum resultado.',
  searchPlaceholder = 'Buscar…',
  disabled,
  id,
  ariaLabel,
  className,
  triggerClassName,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-controls={`${id ?? 'combobox'}-list`}
          disabled={disabled}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-xl px-4 py-2',
            'bg-white dark:bg-neutral-900',
            'border border-neutral-300 dark:border-neutral-700',
            'text-left text-sm',
            'hover:border-neutral-400 dark:hover:border-neutral-600',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-all duration-200',
            triggerClassName
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected ? (
              <>
                {selected.icon && <span className="flex-shrink-0">{selected.icon}</span>}
                <span className="truncate text-foreground">{selected.label}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', className)}
      >
        <CommandPrimitive className="overflow-hidden rounded-xl">
          <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-800 px-3">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <CommandPrimitive.Input
              placeholder={searchPlaceholder}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandPrimitive.List
            id={`${id ?? 'combobox'}-list`}
            className="max-h-64 overflow-y-auto p-1.5"
          >
            <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </CommandPrimitive.Empty>
            {options.map((option) => {
              const keywords = option.keywords ? option.keywords.join(' ') : '';
              return (
                <CommandPrimitive.Item
                  key={option.value}
                  value={`${option.label} ${keywords}`}
                  disabled={option.disabled}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2',
                    'text-sm outline-none',
                    'data-[selected=true]:bg-neutral-100 dark:data-[selected=true]:bg-neutral-800',
                    'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50'
                  )}
                >
                  {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                  <span className="flex flex-1 flex-col">
                    <span>{option.label}</span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </span>
                  {value === option.value && (
                    <Check className="ml-auto h-4 w-4 text-primary-500 dark:text-primary-400" />
                  )}
                </CommandPrimitive.Item>
              );
            })}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );
}
